import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { ReviewService } from '../../../services/review.service';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatLabel } from '@angular/material/form-field';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, Subscription, debounceTime, takeUntil } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportTableConfig } from '../../../models/PdfRelatedModels';
import { ExportTablesToPDFOptions } from '../../../models/ExportTableToPDF';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { MatRadioModule } from '@angular/material/radio';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { ActionNeededField } from '../../../models/ActionNeededFieldChange';
import { CostReviewPlannedCosts, CostReviewPlannedCostsUI } from '../../../models/CostReviewPlannedCosts';
import { CostReviewBaseAttributes } from '../../../models/CostReviewBaseAttributes';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency/multilingual-currency.pipe';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SiDropDownComponent } from '../../Si-Web-Toolkit/si-drop-down/si-drop-down.component';
import { CostReviewSecurityEnUsAttributes } from '../../../models/CostReviewSecurityEnUsAttributes';
import { CostReviewDatabaseRecord } from '../../../models/CostReviewDatabaseRecord';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { SiComboBoxComponent } from '../../Si-Web-Toolkit/si-combo-box/si-combo-box.component';

@Component({
  selector: 'app-sol-costs-labor-tab',
  imports: [
    MatIcon,
    CommonModule,
    MatTableModule,
    MatLabel,
    TranslatePipe,
    MatRadioModule,
    SiFormFieldComponent,
    SiRadioButtonComponent,
    SiComboBoxComponent,
    FormsModule,
    ReactiveFormsModule,
    MultilingualCurrencyPipe,
    DecimalPrecisionPipe,
    MatTooltipModule,
    TrackChangesDirective
  ],
  templateUrl: './sol-costs-labor-tab.component.html',
  styleUrl: './sol-costs-labor-tab.component.scss'
})
export class SolCostsLaborTabComponent implements OnInit, OnDestroy {
  constants = Constants;
  costReviewPlannedCosts: Array<CostReviewPlannedCostsUI> = [];
  costReviewPlannedCostsZtc: Array<CostReviewPlannedCostsUI> = [];
  displayedColumnsItem = ['itemName', 'itemCost', 'tdcPercent', 'riskDisplayText', 'nationalHistoricTDCPercent'];
  displayedColumnsLabor = ['itemName', 'itemCost', 'tdcPercent', 'target', 'nationalHistoricTDCPercent', 'hours'];
  displayedColumnsLaborZones = ['itemName', 'itemCost', 'tdcPercent', 'hours'];
  itemCollection: Array<CostReviewPlannedCostsUI> = [];
  dataSourceItem = new MatTableDataSource(this.itemCollection);
  laborCollection: Array<CostReviewPlannedCostsUI> = [];
  dataSourceLabor = new MatTableDataSource(this.laborCollection);
  reasonsForExceptionDropDownData: Array<{ label: string; value: string }> = [];
  reasonForLaborVarianceDropDownData: Array<{ label: string; value: string }> = [];
  engReasonForexception: string = '';
  engApproverEmailId: string = '';
  engZomApproveReceived: number = this.constants.ZERO;
  isEngineerExceptionApplicable: boolean = false;
  isLaborTDCPercentExceeds: boolean = false;
  reasonForLaborVariance: string = this.constants.EMPTY_STRING;
  business: string = this.constants.EMPTY_STRING;
  isZonePYColumnVisible: boolean = false;
  activeDisplayedColumnsItem: string[] = [];
  @Input() hideSalesOfficeZtcTable: boolean = false;
  @Output() disablePdf = new EventEmitter<boolean>();
  destroy$ = new Subject<void>();
  private formChangesSubscription?: Subscription;
  totalCostItemTable: number = this.constants.ZERO;
  totalPercentOfTDCItemTable: number = this.constants.ZERO;
  totalCostLaborTable: number = this.constants.ZERO;
  totalPercentOfTDCLaborTable: number = this.constants.ZERO;
  activeDisplayedColumnsLabor: string[] = [];

  SolCostAndLaborFormGroup!: FormGroup<{
    engZomApproveReceivedFormControl: FormControl<string>;
    approverEmailId: FormControl<string>;
    reasonForException: FormControl<string>;
    reasonForLaborVariance: FormControl<string>;
  }>;

  isReadOnlyMode: boolean = false;
  isDisabled: boolean = false;
  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly reviewService: ReviewService,
    private readonly translate: TranslateService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailTabsService: ReviewDetailTabsService
  ) { }

  ngOnInit(): void {
    this.business = this.reviewService.getBusiness();
    this.isReadOnlyMode = this.commonDataService.libraryReadOnlyMode;
    this.initializeCostLaborData();
    this.createFormGroup();
    this.setupReadOnlyMode();
    this.subscribeToDataRefresh();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_LABOR_AND_COSTS);
      });
    this.sendDisablePdfEvent();
  }

  private subscribeToDataRefresh(): void {
    this.reviewService.onDataRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeCostLaborData();
        this.patchFormValues();
        const isLibraryUsedInSQ = this.reviewDetailTabsService.getIsLibraryUsedInSQ();
        this.isDisabled = isLibraryUsedInSQ ? this.commonDataService.libraryReadOnlyMode : this.isDisabled;
        this.isReadOnlyMode = isLibraryUsedInSQ ? false : this.commonDataService.libraryReadOnlyMode;
      });
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailTabsService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadOnlyMode = false;
          this.SolCostAndLaborFormGroup.enable();
        } else {
          this.isReadOnlyMode = readOnly;
          this.SolCostAndLaborFormGroup.disable();
        }
        this.formChangesSubscription?.unsubscribe();

        if (!readOnly) {
          this.subscribeToFormChanges();
        }
      });
  }

  createFormGroup() {
    this.SolCostAndLaborFormGroup = this.formBuilder.group({
      engZomApproveReceivedFormControl: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.engZomApproveReceived)),
      approverEmailId: this.formBuilder.control(this.engApproverEmailId),
      reasonForException: this.formBuilder.control(this.engReasonForexception),
      reasonForLaborVariance: this.formBuilder.control(this.reasonForLaborVariance),
    });
  }

  private patchFormValues(): void {
    if (!this.SolCostAndLaborFormGroup) {
      return;
    }

    this.SolCostAndLaborFormGroup.patchValue(
      {
        engZomApproveReceivedFormControl: this.getParameterValues(this.constants.YES_NO, this.engZomApproveReceived),
        approverEmailId: this.engApproverEmailId,
        reasonForException: this.engReasonForexception,
        reasonForLaborVariance: this.reasonForLaborVariance,
      },
      { emitEvent: false }
    );
  }


  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Subscribes to form value changes, writes them back to the model,
   * and notifies the host app per field so it can re-evaluate action-needed visibility.
   */
  private subscribeToFormChanges(): void {
    if (this.isReadOnlyMode) {
      return;
    }

    this.formChangesSubscription = this.SolCostAndLaborFormGroup.valueChanges
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$)
      )
      .subscribe((formValues) => {
        const data: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
        const businessWiseData = this.commonFunctionsService.businessTypePresent(this.business, data);
        if (!businessWiseData) {
          return;
        }

        const newEngApproval = formValues.engZomApproveReceivedFormControl ?? '';
        const previousEngApproval = this.getParameterValues(this.constants.YES_NO, businessWiseData.engineerApprovalReceived);
        if (previousEngApproval !== newEngApproval) {
          const numericValue = this.commonFunctionsService.isValueEqualToTranslation(newEngApproval, 'YES') ? 1 : 2;
          businessWiseData.engineerApprovalReceived = numericValue;
          this.reviewService.notifyFieldChange(ActionNeededField.EngineerApprovalReceived, numericValue);
        }

        const newApproverEmail = formValues.approverEmailId ?? '';
        if (businessWiseData.engineerApproverEmail !== newApproverEmail) {
          businessWiseData.engineerApproverEmail = newApproverEmail;
          this.reviewService.notifyFieldChange(ActionNeededField.EngineerApproverEmail, newApproverEmail);
        }

        const newReason = formValues.reasonForException ?? '';
        if (businessWiseData.engineerReasonForException !== newReason) {
          businessWiseData.engineerReasonForException = newReason;
          this.reviewService.notifyFieldChange(ActionNeededField.EngineerReasonForException, newReason);
        }

        const newLaborVarianceReason = formValues.reasonForLaborVariance ?? '';
        const securityData = businessWiseData as CostReviewSecurityEnUsAttributes;
        if (securityData?.reasonForLaborVariance !== newLaborVarianceReason) {
          securityData.reasonForLaborVariance = newLaborVarianceReason;
          this.reasonForLaborVariance = newLaborVarianceReason;
          this.reviewService.notifyFieldChange(ActionNeededField.SecurityReasonForLaborVariance, newLaborVarianceReason);
        }
      });
  }

  onReasonForLaborVarianceChange(value: string): void {
    this.SolCostAndLaborFormGroup.controls['reasonForLaborVariance'].setValue(value);
  }

  private initializeCostLaborData() {
    let data: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
    const newCostReviewObject = this.reviewService.newCostReviewObject;
    this.costReviewPlannedCosts = [];
    this.costReviewPlannedCostsZtc = [];
    this.itemCollection = [];
    this.laborCollection = [];
    this.business = this.reviewService.getBusiness();
    // Initialize active columns for item table (hide minimum/riskDisplayText for security business)
    const isSecurityBusiness = this.business && this.business.toLowerCase().includes('security');
    this.activeDisplayedColumnsItem = isSecurityBusiness
      ? this.displayedColumnsItem.filter(col => col !== 'riskDisplayText')
      : this.displayedColumnsItem;
    this.activeDisplayedColumnsLabor =
      (this.business === Constants.BUSINESS_AUTOMATION || this.business === Constants.BUSINESS_MECHANICAL)
        ? this.displayedColumnsLabor
        : this.displayedColumnsLabor.filter(col => col !== 'target');


    if (!data || !data.reviewObject || !data.reviewObject) {
      this.dataSourceItem.data = [];
      this.dataSourceLabor.data = [];
      return;
    }
    this.costReviewPlannedCosts = this.mapToUI(data.reviewObject.costReviewPlannedCosts || []);
    this.costReviewPlannedCostsZtc = newCostReviewObject?.costElementsZTC ? this.mapToUI(newCostReviewObject.costElementsZTC) : [];
    this.engReasonForexception = data.reviewObject.engineerReasonForException || Constants.EMPTY_STRING;
    this.engApproverEmailId = data.reviewObject.engineerApproverEmail || Constants.EMPTY_STRING;
    this.engZomApproveReceived = data.reviewObject.engineerApprovalReceived || this.constants.ZERO;
    this.isEngineerExceptionApplicable = data.reviewObject.isEngineerExceptionApplicable;
    this.isLaborTDCPercentExceeds = (data.reviewObject as CostReviewSecurityEnUsAttributes)?.isLaborTDCPercentExceeds || false;
    this.reasonForLaborVariance = (data.reviewObject as CostReviewSecurityEnUsAttributes)?.reasonForLaborVariance || this.constants.EMPTY_STRING;
    this.isZonePYColumnVisible = data.reviewObject.isNationalHistoricalRowVisible || false;
    if (!this.isZonePYColumnVisible) {
      this.activeDisplayedColumnsLabor = this.activeDisplayedColumnsLabor.filter(col => col !== 'nationalHistoricTDCPercent');
      this.activeDisplayedColumnsItem = this.activeDisplayedColumnsItem.filter(col => col !== 'nationalHistoricTDCPercent');
    }
    this.populateData();
    this.populateReasonForExceptionDropdown();
    this.dataSourceItem.data = [...this.itemCollection];
    this.dataSourceLabor.data = [...this.laborCollection];
    this.formatTotalCosts(data.reviewObject);
  }

  populateReasonForExceptionDropdown() {
    if (this.isEngineerExceptionApplicable) {
      this.reasonsForExceptionDropDownData = this.getDropdownOptions('ENGINEER_EXCEPTION_AUTOMATION_');
    }

    if (this.isLaborTDCPercentExceeds) {
      this.reasonForLaborVarianceDropDownData = this.getDropdownOptions('REASON_FOR_LABOR_VARIANCE_');
    }
  }

  private getDropdownOptions(prefix: string): Array<{ label: string; value: string }> {
    return [...this.reviewService.costReviewParameters]
      .filter(p => p.parameterID.startsWith(prefix) && p.description?.trim())
      .map(p => ({
        label: p.description,
        value: p.description
      }));
  }

  private populateData() {
    if (!this.costReviewPlannedCosts?.length) {
      return;
    }

    this.costReviewPlannedCosts.forEach((item) => {
      if (!item) {
        return;
      }

      item = this.appendSymbol(item);
      if (item.positionId < this.constants.TWENTY) {
        this.itemCollection.push(item);
      } else {
        this.laborCollection.push(item);
      }
    });
  }

  private formatTotalCosts(crba: CostReviewBaseAttributes) {
    if (!crba) {
      return;
    }

    crba.itemCostTotal = this.itemCollection.reduce((sum, item) => sum + (item.itemCost || Constants.ZERO), Constants.ZERO);
    crba.itemTDCTotal = this.itemCollection.reduce((sum, item) => sum + (item.tdcPercent || Constants.ZERO), Constants.ZERO);
    crba.costTotal = this.laborCollection.reduce((sum, item) => sum + (item.itemCost || Constants.ZERO), Constants.ZERO);
    crba.tdcTotal = this.laborCollection.reduce((sum, item) => sum + (item.tdcPercent || Constants.ZERO), Constants.ZERO);

    this.totalCostItemTable = crba.itemCostTotal || Constants.ZERO;
    this.totalPercentOfTDCItemTable = crba.itemTDCTotal || Constants.ZERO;
    this.totalCostLaborTable = crba.costTotal || Constants.ZERO;
    this.totalPercentOfTDCLaborTable = crba.tdcTotal || Constants.ZERO;
  }

  private mapToUI(items: CostReviewPlannedCosts[]): CostReviewPlannedCostsUI[] {
    return items.map(item => ({ ...item, isFlagVisible: false }));
  }

  private appendSymbol(item: CostReviewPlannedCostsUI): CostReviewPlannedCostsUI {
    let showFlag = ((item.itemName === this.constants.COST_LABOR_ENGINEER) && (item.tdcPercent < item.target));
    // item.tdcPercent = item.tdcPercent;
    item.nationalHistoricTDCPercent = item.nationalHistoricTDCPercent ? item.nationalHistoricTDCPercent : this.constants.ZERO;
    item.riskDisplayText = item.riskDisplayText && (item.itemName === this.constants.COST_LABOR_RISK) ? item.riskDisplayText : this.constants.ZERO;
    item.target = item.target && (item.itemName === this.constants.COST_LABOR_ENGINEER) ? item.target : this.constants.ZERO;
    item.isFlagVisible = showFlag;
    return item;
  }

  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, value);
  }


  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName === Constants.SOL_LABOR_AND_COSTS) {

      // Add flag to riskDisplayText if isFlagVisible is true
      const itemDataForPdf = this.dataSourceItem.data && this.dataSourceItem.data.length > Constants.ZERO
        ? this.dataSourceItem.data.map(item => ({
          ...item,
          riskDisplayText: item.riskDisplayText + (item.isFlagVisible ? ' [[ASTRIX]]' : Constants.EMPTY_STRING)
        }))
        : [this.getNoDataRow(this.displayedColumnsItem.filter(col => col !== 'nationalHistoricTDCPercent'), 'No data available')];

      // For Labor Cost Elements
      const itemDataForPdfLabor = this.dataSourceLabor.data && this.dataSourceLabor.data.length > Constants.ZERO
        ? this.dataSourceLabor.data.map(item => ({
          ...item,
          target: item.target + (item.isFlagVisible ? ' [[ASTRIX]]' : Constants.EMPTY_STRING)
        }))
        : [this.getNoDataRow(this.displayedColumnsLabor.filter(col => col !== 'nationalHistoricTDCPercent'), 'No data available')];
      const showMinimumColumn = itemDataForPdfLabor.some(item => item.target && item.target !== Constants.ZERO_DECIMAL_PERCENT);

      const displayedColumnsLabor = this.displayedColumnsLabor
        .filter(col => col !== 'nationalHistoricTDCPercent' && (showMinimumColumn || col !== 'target'));

      const headersLabor = {
        itemName: this.translate.instant('REVIEW_SOL_LABOR_AND_COSTS_TAB.LABOR'),
        itemCost: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.COST'),
        tdcPercent: this.translate.instant('REVIEW_SOL_RISK_TAB.TDC_PERCENT'),
        ...(showMinimumColumn ? { target: this.translate.instant('REVIEW_SOL_MARGINS_TAB.MINIMUM') } : {}),
        hours: this.translate.instant('REVIEW_SOL_LABOR_AND_COSTS_TAB.HOURS')
      };

      const tables: ExportTableConfig[] = [
        {
          title: 'Labor Cost Elements',
          displayedColumns: displayedColumnsLabor,
          headers: headersLabor,
          data: itemDataForPdfLabor,
          typeGuard: (row: CostReviewPlannedCosts, col: string) => row[col as keyof CostReviewPlannedCosts]
        },
        {
          title: 'Item Cost Elements',
          displayedColumns: this.displayedColumnsItem.filter(col => col !== 'nationalHistoricTDCPercent'),
          headers: {
            itemName: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ITEM'),
            itemCost: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.COST'),
            tdcPercent: this.translate.instant('REVIEW_SOL_RISK_TAB.TDC_PERCENT'),
            riskDisplayText: this.translate.instant('REVIEW_SOL_MARGINS_TAB.MINIMUM')
          },
          data: itemDataForPdf.length > Constants.ZERO ? itemDataForPdf : [this.getNoDataRow(this.displayedColumnsItem.filter(col => col !== 'nationalHistoricTDCPercent'), this.translate.instant('REVIEW_SOL_LABOR_AND_COSTS_TAB.LABOR_AND_COSTS_DATA_NOT_FOUND'))],
          typeGuard: (row: CostReviewPlannedCosts, col: string) => row[col as keyof CostReviewPlannedCosts]
        },
      ];

      const options: ExportTablesToPDFOptions = {
        tables,
        fileName: 'labor-cost-elements.pdf',
        hasNotes: false,
        estimateID: selectedReview.estimateID,
        estimateName: selectedReview.request,
        tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.LABOR_AND_COSTS'),
      };
      this.commonFunctionsService.exportTablesToPDF(options);
    }
  }

  getNoDataRow(columns: string[], message: string): Record<string, string> {
    const row: Record<string, string> = {};
    columns.forEach((col, idx) => {
      row[col] = idx === 0 ? message : Constants.EMPTY_STRING;
    });
    return row;
  }
}
